import { readStoredRefreshToken, useSession } from '../auth/session';

/**
 * Client HTTP theo `05-api-conventions.md`.
 *
 * Kiểu dữ liệu của từng endpoint KHÔNG viết tay ở đây — sinh từ OpenAPI bằng `npm run gen:api`
 * (ra `src/api/schema.d.ts`), rồi dùng `paths[...]` để lấy kiểu. Xem `src/api/README.md`.
 */
const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

/** Envelope thành công: `{ data, meta }` */
export interface ApiEnvelope<T> {
  data: T;
  meta?: PaginationMeta;
}

export interface PaginationMeta {
  page: number;
  pageSize: number;
  total: number;
}

/** Envelope lỗi: `{ error: { code, message, details } }` */
export interface ApiErrorBody {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

/**
 * Lỗi nghiệp vụ từ server. Code là SCREAMING_SNAKE và ổn định —
 * UI phân nhánh theo `code`, KHÔNG theo `message` (05-api-conventions).
 * `message` đã là tiếng Việt hiển thị thẳng được.
 */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details?: Record<string, unknown>;

  constructor(body: ApiErrorBody, status: number) {
    super(body.message);
    this.name = 'ApiError';
    this.code = body.code;
    this.status = status;
    this.details = body.details;
  }
}

/** Lỗi mạng/timeout — khác hẳn lỗi nghiệp vụ, UI phải mời thử lại chứ không hiện message server. */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super('Không kết nối được máy chủ. Kiểm tra mạng rồi thử lại.');
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Bắt buộc cho mọi POST tạo giao dịch (05-api-conventions) */
  idempotencyKey?: string;
  /**
   * Bỏ header Authorization. Dùng cho đăng nhập và refresh — gửi kèm access token
   * đã hết hạn ở đó chỉ tổ nhận 401 rồi tự đăng xuất giữa lúc đang khôi phục phiên.
   */
  skipAuth?: boolean;
  /** Đã thử refresh một lần cho request này — không lặp. */
  retriedAfterRefresh?: boolean;
}

let refreshInFlight: Promise<boolean> | null = null;

async function refreshAccessToken(): Promise<boolean> {
  if (refreshInFlight !== null) return refreshInFlight;

  refreshInFlight = (async () => {
    const refreshToken = readStoredRefreshToken();
    if (refreshToken === null) return false;

    try {
      const envelope = await apiRequest<{ accessToken: string; refreshToken: string }>(
        '/auth/refresh',
        { method: 'POST', body: { refreshToken }, skipAuth: true },
      );
      useSession.getState().renewAccessToken(envelope.data.accessToken, envelope.data.refreshToken);
      return true;
    } catch {
      return false;
    }
  })().finally(() => {
    refreshInFlight = null;
  });

  return refreshInFlight;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<ApiEnvelope<T>> {
  const { body, idempotencyKey, skipAuth, retriedAfterRefresh, headers, ...rest } = options;
  const token = skipAuth ? undefined : useSession.getState().session?.accessToken;

  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      ...rest,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
        ...headers,
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (cause) {
    throw new NetworkError(cause);
  }

  if (response.status === 401) {
    // Access hết hạn nhưng refresh còn: lấy token mới rồi gửi lại. Không refresh thì
    // Admin Web 8 giờ vẫn bị đá nếu tab để qua mốc exp; Staff/Member cũng cùng client này.
    if (!skipAuth && token && retriedAfterRefresh !== true) {
      const renewed = await refreshAccessToken();
      if (renewed) {
        return apiRequest<T>(path, { ...options, retriedAfterRefresh: true });
      }
    }
    useSession.getState().signOut();
  }

  if (!response.ok) {
    const parsed = (await response.json().catch(() => null)) as { error?: ApiErrorBody } | null;
    throw new ApiError(
      parsed?.error ?? { code: 'UNKNOWN_ERROR', message: `Máy chủ trả lỗi ${response.status}.` },
      response.status,
    );
  }

  if (response.status === 204) return { data: undefined as T };

  return (await response.json()) as ApiEnvelope<T>;
}

/** Lấy thẳng phần `data`, dùng cho phần lớn query không cần `meta`. */
export async function apiGet<T>(path: string, options?: RequestOptions): Promise<T> {
  const envelope = await apiRequest<T>(path, { ...options, method: 'GET' });
  return envelope.data;
}

/**
 * POST multipart (tải tệp). Khác `apiRequest`: KHÔNG đặt `Content-Type` để trình duyệt tự gắn
 * `boundary` của multipart, và gửi thẳng `FormData` thay vì JSON. Vẫn kèm token + thử refresh một
 * lần khi 401, giống `apiRequest`.
 */
export async function apiUpload<T>(
  path: string,
  formData: FormData,
  retriedAfterRefresh = false,
): Promise<T> {
  const token = useSession.getState().session?.accessToken;

  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
  } catch (cause) {
    throw new NetworkError(cause);
  }

  if (response.status === 401) {
    if (token && !retriedAfterRefresh) {
      const renewed = await refreshAccessToken();
      if (renewed) return apiUpload<T>(path, formData, true);
    }
    useSession.getState().signOut();
  }

  if (!response.ok) {
    const parsed = (await response.json().catch(() => null)) as { error?: ApiErrorBody } | null;
    throw new ApiError(
      parsed?.error ?? { code: 'UNKNOWN_ERROR', message: `Máy chủ trả lỗi ${response.status}.` },
      response.status,
    );
  }

  return ((await response.json()) as ApiEnvelope<T>).data;
}
